import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo } from '@fortawesome/free-solid-svg-icons';
import { FormGroup, FormControl, FormLabel, Image, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import config from '../config/config';
import { useWebsite, setFlashMessage } from '../contexts/WebsiteContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import MediaItem from '../components/MediaItem';
import MediaForm from '../components/MediaForm';
import { ucfirst } from '../utils/utils';
import { useAuth } from '../contexts/AuthContext';

function MediaList({ mediaKey, maxItems, isShortLine = false }) {
    const { t } = useTranslation();
    const { isModeAdmin } = useMode();
    const { website, setIsLoading } = useWebsite();
    const { token } = useAuth();
    const { restoData } = useResto();
    const availableLanguages = restoData.availableLanguages;

    const { apiUrl } = config;
    const apiPath = `${apiUrl}media`;

    const [mediaList, setMediaList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const initialFormData = {
        labelFr: '',
        labelEn: '',
        labelDe: '',
        labelEs: '',
        detailsFr: '',
        detailsEn: '',
        detailsDe: '',
        detailsEs: '',
        videoUrl: '',
        file: null,
        mediaKey: mediaKey,
        website: website,
    }
    const [formData, setFormData] = useState(initialFormData);

    const fetchItems = async () => {
        try {
            const response = await axios.get(`${apiPath}?mediaKey=${mediaKey}&website=${website}`);
            if (response.data) {
                setMediaList(response.data.slice(0, maxItems));
            }
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
        }
    };

    useEffect(() => {
        fetchItems();
    }, [mediaKey, maxItems]);

    const handleAddPhoto = () => {
        setSelectedAction('photo');
        setShowModal(true);
    };
    
    const handleAddVideo = () => {
        setSelectedAction('video');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAction(null);
        setFormData(initialFormData);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData((prevState) => ({
            ...prevState,
            file: file,
        }));
    };

    const handleVideoUrlChange = (e) => {
        const { value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            videoUrl: value,
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            const formDataWithMedia = new FormData();
            for (let key in formData) {
                formDataWithMedia.append(key, formData[key]);
            }
            formDataWithMedia.append('mediaType', selectedAction);
            await axios.post(`${config.apiUrl}media`, formDataWithMedia, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            fetchItems();
            setShowModal(false);
            setFlashMessage(t('SUCCESS_CREATED_MESSAGE'), 'success');
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
            console.error('An error occurred while adding item:', error);
        }
        setIsLoading(false);
        setFormData(initialFormData);
    };

    const handleDelete = async (mediaId) => {
        setIsLoading(true);
        try {
            await axios.delete(`${apiPath}/${mediaId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            fetchItems();
            setFlashMessage(t('SUCCESS_DELETED_MESSAGE'), 'success');
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
            console.error('Error deleting media:', error);
        }
        setIsLoading(false);
    };

    const renderInputFields = (language) => {
        return (
            <>
                <FormGroup>
                    <FormLabel htmlFor={`label${ucfirst(language)}`}>Label <Image src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`} className="flag-label" alt={`Label ${language}`} /></FormLabel>
                    <FormControl type="text" id={`label${ucfirst(language)}`} name={`label${ucfirst(language)}`} value={formData[`label${ucfirst(language)}`]} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                    <FormLabel htmlFor={`details${ucfirst(language)}`}>Détails <Image src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`} className="flag-label" alt={`Label ${language}`} /></FormLabel>
                    <FormControl as="textarea" rows={3} id={`details${ucfirst(language)}`} name={`details${ucfirst(language)}`} value={formData[`details${ucfirst(language)}`]} onChange={handleChange} />
                </FormGroup>
            </>
        );
    };

    const handleRankingUpdate = async (direction, itemId) => {
        setIsLoading(true);
        try {
            await axios.put(
                `${apiPath}/${itemId}`,
                { ranking: direction }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
            );
            fetchItems();
            setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success');
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
            console.error(`Error updating item ranking (${direction}):`, error);
        }
        setIsLoading(false);
    };

    const handleEdit = async (editedItem, itemId) => {
        setIsLoading(true);
        try {
            await axios.put(
                `${apiPath}/${itemId}`,
                editedItem, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
            );
            fetchItems();
            setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success');
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
            console.error(`Error updating item:`, error);
        }
        setIsLoading(false);
    };

    return (
        <div style={{ marginTop: '20px' }}>
            {mediaList.map((media, index) => (
                <MediaItem key={index}
                    media={media}
                    handleDelete={() => handleDelete(media.id)}
                    handleEdit={(editedItem) => handleEdit(editedItem, media.id)}
                    handleRankingUpdate={(direction) => handleRankingUpdate(direction, media.id)}
                    isFirst={index === 0}
                    isLast={index === mediaList.length - 1}
                    isShortLine={isShortLine || (index !== mediaList.length - 1)} />
            ))}
            {mediaList.length < maxItems && isModeAdmin && (
                <div className="text-center">
                    <Button variant="primary" onClick={handleAddPhoto} style={{ margin: '0 5px 10px 5px' }}>
                        <FontAwesomeIcon icon={faImage} /> Ajouter une photo ici
                    </Button>
                    <Button variant="primary" onClick={handleAddVideo} style={{ margin: '0 5px 10px 5px' }}>
                        <FontAwesomeIcon icon={faVideo} /> Ajouter une vidéo YouTube ici
                    </Button>
                </div>
            )}

            <MediaForm
                showModal={showModal}
                handleCloseModal={handleCloseModal}
                handleSubmit={handleSubmit}
                handleFileChange={handleFileChange}
                handleVideoUrlChange={handleVideoUrlChange}
                renderInputFields={renderInputFields}
                availableLanguages={availableLanguages}
                selectedAction={selectedAction}
            />
        </div>
    );
}

export default MediaList;
